import { Box, Button, CircularProgress, Collapse, Container, Skeleton, Stack, SwipeableDrawer, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";

import * as React from "react";
import { useHistory } from "react-router-dom";
import { CourseApi, ICourse } from "../api/CourseApi";
import { DialogApi, IDialog } from "../api/DialogApi";
import { ILesson, LessonApi } from "../api/LessonApi";
import CourseView from "../component/CourseView";
import DialogView from "../component/DialogView";
import { handleErrorMessage } from '../component/ErrorDialog';
import LessonView from "../component/LessonView";
import SettingsView from "../component/SettingsView";
import AppBarContext, { AppBarMenuIndex } from '../context/AppBarContext';

import LargeBlackIcon from "../asset/icon-menu-large-black.png";
import LargeWhiteIcon from "../asset/icon-menu-large-white.png";
import MediumOrangeIcon from "../asset/icon-menu-medium-orange.png";
import MediumWhiteIcon from "../asset/icon-menu-medium-white.png";
import SmallOrangeIcon from "../asset/icon-menu-small-orange.png";
import SmallWhiteIcon from "../asset/icon-menu-small-white.png";
import SidebarCollapseIcon from "../asset/icon-sidebar-collapse.png";
import SidebarExpandIcon from "../asset/icon-sidebar-expand.png";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";

import { useLocation } from "react-router-dom";
import { UserDialogPromptApi, UserDialogPromptReviewResponse, UserDialogPromptUserSummary } from "../api/UserDialogPromptApi";
import SettingsOrangeIcon from "../asset/settings-outline-large-orange.png";
import SettingsWhiteIcon from "../asset/settings-outline-large-white.png";

import { ActivityEnum, useUserActivityTracker } from "../UserActivityTracker";
import { DialogPrompt } from "../api/DialogPromptApi";
import ReviewView, { isReviewLesson, isReviewLessonHidden } from "../component/ReviewView";
import { DialogPromptFooterProvider } from "../context/DialogPromptFooterContext";
import { LessonProvider } from "../context/LessonContext";

export function isSettingsComplete() {
    return localStorage.getItem("studentName") != null && localStorage.getItem("studentAvatar") != null && localStorage.getItem("teacher") != null;
}

export function isSettingsCourse(course: ICourse) {
    return course.name === "Settings";
}

export function isReviewCourse(course: ICourse) {
    return course.name === "Review";
}


export default function Lesson() {

    const [courseList, setCourseList] = React.useState<ICourse[]>([]);
    const [selectedCourse, setSelectedCourse] = React.useState<ICourse | null>(null);

    const [userActivityCourseId, setUserActivityCourseId] = React.useState<number | null>(null);
    useUserActivityTracker(
        selectedCourse != null && isSettingsCourse(selectedCourse) ? ActivityEnum.Settings : ActivityEnum.Lesson,
        null,
        selectedCourse != null && isSettingsCourse(selectedCourse) ? null : userActivityCourseId
    )

    const { setErrorMessage } = React.useContext(AppBarContext);
    const history = useHistory();
    const location = useLocation();

    const { setMenuIndex } = React.useContext(AppBarContext);

    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);

    const [lessonList, setLessonList] = React.useState<ILesson[]>([]);
    const [selectedLesson, setSelectedLesson] = React.useState<ILesson | null>(null);

    const [dialogList, setDialogList] = React.useState<IDialog[]>([]);
    const [selectedDialog, setSelectedDialog] = React.useState<IDialog | null>(null);

    const [userDialogPromptReviewResponse, setUserDialogPromptReviewResponse] = React.useState<UserDialogPromptReviewResponse | null>(null);

    const [lastRequestParam, setLastRequestParamUsed] = React.useState("");

    const [initialSummaryLoaded, setInitialSummaryLoaded] = React.useState(false);

    const [nextDialogPrompt, setNextDialogPrompt] = React.useState<DialogPrompt | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isTouchDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && window.matchMedia("(pointer: coarse)").matches;

    const [isMenuCollapsed, setIsMenuCollapsed] = React.useState(false);
    const courseRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});
    const lessonRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});
    const dialogRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});
    const locationRef = React.useRef(location);
    React.useEffect(() => { locationRef.current = location; });

    // this is for DialogPromptSummaryComponent

    // Auto-collapse sidebar on mobile screens
    React.useEffect(() => {
        setIsMenuCollapsed(isMobile);
    }, [isMobile]);

    const onToggleMenuCollapse = () => setIsMenuCollapsed(!isMenuCollapsed);

    React.useEffect(() => {
        setMenuIndex(AppBarMenuIndex.Lesson);
        const skeletonTimeout = setTimeout(() => setShowSkeleton(true), 300); // 300ms delay before showing shimmer

        Promise.all([CourseApi.getAll(), LessonApi.getAll(), DialogApi.getAll()])
            .then(([courseResponse, lessonResponse, dialogResponse]) => {
                if (courseResponse.status < 300) setCourseList(courseResponse.data.filter(c => !c.is_hidden));
                if (lessonResponse.status < 300) setLessonList(lessonResponse.data.filter(l => !l.is_hidden));
                if (dialogResponse.status < 300) setDialogList(dialogResponse.data.filter(d => !d.is_hidden));
            })
            .catch((err) => handleErrorMessage(err, setErrorMessage, history, locationRef.current))
            .finally(() => {
                setIsLoading(false);
                clearTimeout(skeletonTimeout);
            });

        UserDialogPromptApi.getNextDialogPrompt()
            .then((response) => {
                if (response.status < 300) {
                    return response.data;
                }
                console.error(response);
                handleErrorMessage(response, setErrorMessage, history, locationRef.current);
                return null;
            })
            .then((dialogPrompt) => {
                if (dialogPrompt) {
                    setNextDialogPrompt(dialogPrompt);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setErrorMessage, setMenuIndex]);

    React.useEffect(() => {
        if (initialSummaryLoaded == false && courseList.length > 0 && lessonList.length > 0 && dialogList.length > 0) {
            setInitialSummaryLoaded(true);
            UserDialogPromptApi.getCurrentUserSummary()
                .then((response) => {
                    if (response.status < 300) {
                        return response.data;
                    }
                    throw new Error("Unable to UserDialogPromptApi.getCurrentUserSummary " + response.status);
                })
                .then((summary) => {
                    updateUserDialogPromptSummary(summary);
                })
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        }

        // URL sync must not require dialogs to exist (e.g. course/lesson-only pages).
        if (courseList.length > 0 && lessonList.length > 0) {
            const pathParts = location.pathname.split('/').filter(Boolean);

            if (pathParts[0] === 'lesson' && pathParts.length === 1) {
                // Reopen the sidebar if back to /lesson on mobile
                if (isMobile || isTouchDevice) {
                    setIsMenuCollapsed(false);
                }
            }

            // Only update if the path actually changed
            if (pathParts.join('/') !== lastRequestParam) {
                // Reset selections first
                setSelectedCourse(null);
                setSelectedLesson(null);
                setSelectedDialog(null);

                try {
                    if (pathParts[0] === 'lesson') {
                        if (pathParts[1] === 'settings') {
                            const settingsCourse = courseList.find(c => isSettingsCourse(c));
                            if (settingsCourse) {
                                setSelectedCourse(settingsCourse);
                                setCourseList(prev => prev.map(c =>
                                    isSettingsCourse(c) ? { ...c, expanded: true } : c
                                ));
                            } else {
                                throw new Error("Settings course not found");
                            }
                        } else {
                            // Handle course ID
                            const courseId = pathParts[1] ? parseInt(pathParts[1], 10) : null;
                            if (courseId) {
                                const course = courseList.find(c => c.id === courseId);
                                if (course) {
                                    setSelectedCourse(course);
                                    setCourseList(prev => prev.map(c =>
                                        c.id === courseId ? { ...c, expanded: true } : c
                                    ));
                                }
                            }

                            // Handle lesson ID if present
                            const lessonId = pathParts[2] ? parseInt(pathParts[2], 10) : null;
                            if (lessonId) {
                                const lesson = lessonList.find(l => l.id === lessonId);
                                if (lesson) {
                                    setSelectedLesson(lesson);
                                    setLessonList(prev => prev.map(l =>
                                        l.id === lessonId ? { ...l, expanded: true } : l
                                    ));
                                    // Also expand the parent course
                                    setCourseList(prev => prev.map(c =>
                                        c.id === lesson.course_id ? { ...c, expanded: true } : c
                                    ));
                                }
                            }

                            // Handle dialog ID if present
                            const dialogId = pathParts[3] ? parseInt(pathParts[3], 10) : null;
                            if (dialogId) {
                                const dialog = dialogList.find(d => d.id === dialogId);
                                if (dialog) {
                                    // Dialog deep links must respect hidden ancestors. The lists in this
                                    // component are already visibility-filtered, so missing parent records
                                    // mean the dialog should not be playable from a direct URL.
                                    const lesson = lessonList.find(l => l.id === dialog.lesson_id);
                                    if (lesson) {
                                        const course = courseList.find(c => c.id === lesson.course_id);
                                        if (course) {
                                            setSelectedDialog(dialog);
                                            setDialogList(prev => prev.map(d =>
                                                d.id === dialogId ? { ...d, expanded: true } : d
                                            ));
                                            // Also expand the parent lesson and course
                                            setLessonList(prev => prev.map(l =>
                                                l.id === lesson.id ? { ...l, expanded: true } : l
                                            ));
                                            setCourseList(prev => prev.map(c =>
                                                c.id === lesson.course_id ? { ...c, expanded: true } : c
                                            ));
                                        }
                                    }
                                }
                            }
                        }
                    }
                } catch (err) {
                    handleErrorMessage(err, setErrorMessage, history, location);
                }
                setLastRequestParamUsed(pathParts.join('/'));
            }
        }
    }, [location.pathname, courseList, lessonList, dialogList, history, initialSummaryLoaded, isMobile, isTouchDevice, lastRequestParam, location, setErrorMessage]);

    React.useEffect(() => {
        const hasNoQueryParams = location.search === "";
        const pathParts = location.pathname.split('/').filter(Boolean);
        const hasDialogInUrl = pathParts.length >= 4 && pathParts[0] === 'lesson';

        // Don't override URL-driven selection with nextDialogPrompt
        if (hasDialogInUrl) return;

        // Explicit /lesson/:courseId(/...) — respect deep link; do not resume "next" dialog.
        const hasExplicitLessonRoute =
            pathParts[0] === "lesson" && pathParts.length >= 2 && pathParts[1] !== "settings";
        if (hasExplicitLessonRoute) {
            return;
        }

        if (hasNoQueryParams && isSettingsComplete() && nextDialogPrompt) {
            const nextDialog = dialogList.find((dialog) => dialog.id == nextDialogPrompt.dialog_id);
            if (nextDialog && selectedDialog?.id !== nextDialog.id) {
                setSelectedDialog(nextDialog);
            }
        }
    }, [nextDialogPrompt, location.pathname, location.search, dialogList, selectedDialog]);

    /*
    User Activity
    */
    React.useEffect(() => {
        if (selectedCourse && selectedCourse.id != userActivityCourseId) {
            setUserActivityCourseId(selectedCourse.id)
        }
    }, [selectedCourse, userActivityCourseId])

    React.useEffect(() => {
        if (selectedLesson && selectedLesson.course_id != userActivityCourseId) {
            setUserActivityCourseId(selectedLesson.course_id)
        }
    }, [selectedLesson, userActivityCourseId])

    React.useEffect(() => {
        if (selectedDialog) {
            const lesson = lessonList.find((l) => l.id === selectedDialog.lesson_id);
            if (lesson && lesson.course_id != userActivityCourseId) {
                setUserActivityCourseId(lesson.course_id)
            }
        }
    }, [selectedDialog, lessonList, userActivityCourseId])

    /* Scroll to selected item */
    React.useEffect(() => {
        // Use setTimeout to ensure the DOM has updated before scrolling
        const timer = setTimeout(() => {
            if (selectedDialog && dialogRefs.current[selectedDialog.id]) {
                const dialogElement = dialogRefs.current[selectedDialog.id];
                if (dialogElement) {
                    dialogElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            } else if (selectedLesson && lessonRefs.current[selectedLesson.id]) {
                const lessonElement = lessonRefs.current[selectedLesson.id];
                if (lessonElement) {
                    lessonElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            } else if (selectedCourse && courseRefs.current[selectedCourse.id]) {
                const courseElement = courseRefs.current[selectedCourse.id];
                if (courseElement) {
                    courseElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [selectedCourse, selectedLesson, selectedDialog]);

    /*
          SETTINGS HANDLERS
       */

    const [jiggling, setJiggling] = React.useState(false);

    const triggerJiggle = () => {
        setJiggling(true);
        setTimeout(() => setJiggling(false), 400); // match animation duration
    };

    const jiggleKeyframes = {
        '@keyframes jiggle': {
            '0%': { transform: 'rotate(0deg)' },
            '25%': { transform: 'rotate(3deg)' },
            '50%': { transform: 'rotate(-3deg)' },
            '75%': { transform: 'rotate(3deg)' },
            '100%': { transform: 'rotate(0deg)' },
        },
    };

    function setSelectedCourseToSettings() {
        const settingsCourse = courseList.find((course) => isSettingsCourse(course));
        //set and unset to force rerender of SettingsPage.
        clearLessonMenu()
        clearDialogMenu()
        setSelectedCourse(null);
        setSelectedLesson(null);
        setSelectedDialog(null);
        if (settingsCourse) {
            setSelectedCourse(settingsCourse);
        }
    }

    /*
          CLEAR
      */

    const clearLessonMenu = () => {
        const updatedLessonList = lessonList.map((course) => ({
            ...course, // Spread operator to copy properties of the current course object
            expanded: false, // Set the expanded property to false
        }));
        setLessonList(updatedLessonList);
    };
    const clearDialogMenu = () => {
        const updatedDialogList = dialogList.map((course) => ({
            ...course, // Spread operator to copy properties of the current course object
            expanded: false, // Set the expanded property to false
        }));
        setDialogList(updatedDialogList);
    };

    /*
          COURSE HANDLERS
       */
    const handleCourseArrowClick = (e: React.MouseEvent, clickedCourse: ICourse, expand: boolean | null = null) => {
        e.stopPropagation(); // prevent row click from triggering

        setCourseList((prevCourseList) =>
            prevCourseList.map((course) =>
                course.id === clickedCourse.id
                    ? { ...course, expanded: expand === null ? !course.expanded : expand } // toggle or force expand/collapse
                    : course // leave other courses untouched
            )
        );
    };

    const handleCourseMenuClick = (course: ICourse) => {
        setNextDialogPrompt(null);

        if (!isSettingsComplete()) {
            setSelectedCourseToSettings();
            triggerJiggle();
            return;
        }

        setSelectedLesson(null);
        setSelectedCourse(course);

        // Navigate to the course page
        history.push(`/lesson/${course.id}`);

        if (isReviewCourse(course)) {
            updateUserDialogPromptReview();
        }

        // Clear any selected dialog
        setSelectedDialog(null);

        if (isMobile || isTouchDevice) {
            onToggleMenuCollapse();
        }
    };

    const updateUserDialogPromptReview = () => {
        UserDialogPromptApi.getUserDialogPromptReview()
            .then(response => {
                if (response.status < 300) {
                    return response.data;
                }
                throw new Error("UserDialogPromptApi.getUserDialogPromptReview() " + response.status)

            })
            .then(userDialogPromptReviewResponse => {

                const reviewCourse = courseList.find((course) => course.name.toLowerCase() === "review");
                setLessonList((prevLessonList) =>
                    prevLessonList.map((lesson) => {
                        const is_hidden = isReviewLessonHidden(userDialogPromptReviewResponse, lesson, reviewCourse);
                        return {
                            ...lesson,
                            is_hidden: is_hidden
                        };
                    })
                );
                setUserDialogPromptReviewResponse(userDialogPromptReviewResponse)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }
    /*
          LESSON HANDLERS
       */
    const handleLessonArrowClick = (e: React.MouseEvent, clickedLesson: ILesson, expand: boolean | null = null) => {
        e.stopPropagation(); // prevent row click
        setLessonList((prevLessonList) =>
            prevLessonList.map((lesson) =>
                lesson.id === clickedLesson.id
                    ? { ...lesson, expanded: expand === null ? !lesson.expanded : expand }
                    : lesson
            )
        );
    };

    const handleLessonMenuClick = (lesson: ILesson) => {
        setNextDialogPrompt(null);

        if (!isSettingsComplete()) {
            setSelectedCourseToSettings();
            triggerJiggle();
            return;
        }

        setSelectedDialog(null);

        // Select the lesson
        setSelectedLesson(lesson);

        // Navigate to /lesson/:courseId/:lessonId
        const course = courseList.find(c => c.id === lesson.course_id);
        if (course) {
            history.push(`/lesson/${course.id}/${lesson.id}`);
        }

        if (isMobile || isTouchDevice) {
            onToggleMenuCollapse();
        }
    };

    /*
            DIALOG HANDLERS
        */
    const handleDialogMenuClick = async (dialog: IDialog) => {
        setNextDialogPrompt(null);

        if (!isSettingsComplete()) {
            setSelectedCourseToSettings();
            triggerJiggle();
            return;
        }

        // Find the lesson and course for this dialog
        const lesson = lessonList.find(l => l.id === dialog.lesson_id);
        const course = lesson ? courseList.find(c => c.id === lesson.course_id) : null;

        setSelectedDialog(dialog);

        // Navigate to /lesson/:courseId/:lessonId/:dialogId
        if (course && lesson) {
            history.push(`/lesson/${course.id}/${lesson.id}/${dialog.id}`);
        }

        if (isMobile || isTouchDevice) {
            onToggleMenuCollapse();
        }
    };

    const updateUserDialogPromptSummary = (summaryList: UserDialogPromptUserSummary[]) => {
        const courseCount: Record<number, [number, number]> = {};
        const lessonCount: Record<number, [number, number]> = {};
        const dialogCount: Record<number, [number, number]> = {};

        summaryList.forEach((item) => {
            const { course_id, lesson_id, dialog_id, dialog_prompt_count, user_dialog_prompt_count } = item;

            // Update courseCount
            if (!courseCount[course_id]) {
                courseCount[course_id] = [0, 0];
            }
            courseCount[course_id][0] += dialog_prompt_count;
            courseCount[course_id][1] += user_dialog_prompt_count;

            // Update lessonCount
            if (!lessonCount[lesson_id]) {
                lessonCount[lesson_id] = [0, 0];
            }
            lessonCount[lesson_id][0] += dialog_prompt_count;
            lessonCount[lesson_id][1] += user_dialog_prompt_count;

            // Update dialogCount
            if (!dialogCount[dialog_id]) {
                dialogCount[dialog_id] = [0, 0];
            }
            dialogCount[dialog_id][0] += dialog_prompt_count;
            dialogCount[dialog_id][1] += user_dialog_prompt_count;
        });

        setCourseList((prevCourseList) =>
            prevCourseList.map((course) => {
                const counts = courseCount[course.id];
                return {
                    ...course,
                    dialogPromptCount: counts ? counts[0] : 0,
                    userDialogPromptCount: counts ? counts[1] : 0,
                };
            })
        );

        setLessonList((prevLessonList) =>
            prevLessonList.map((lesson) => {
                const counts = lessonCount[lesson.id];
                return {
                    ...lesson,
                    dialogPromptCount: counts ? counts[0] : 0,
                    userDialogPromptCount: counts ? counts[1] : 0,
                };
            })
        );

        setDialogList((prevDialogList) =>
            prevDialogList.map((dialog) => {
                const counts = dialogCount[dialog.id];
                return {
                    ...dialog,
                    dialogPromptCount: counts ? counts[0] : 0,
                    userDialogPromptCount: counts ? counts[1] : 0,
                };
            })
        );
    };

    const calculateUserDialogPromptIcon = (userDialogPromptCount: number | undefined, dialogPromptCount: number | undefined) => {
        if (!userDialogPromptCount || !dialogPromptCount) {
            return <div />;
        }

        const percentComplete = Math.round((userDialogPromptCount / dialogPromptCount) * 100);
        const tooltipText = `${userDialogPromptCount} of ${dialogPromptCount} complete`;

        return (
            <Tooltip title={tooltipText} arrow>
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                        variant="determinate"
                        value={percentComplete}
                        size={32}
                        thickness={10}
                        sx={{
                            color: percentComplete === 100 ? "#04A755" : "#ffffff",
                        }}
                    />
                </Box>
            </Tooltip>
        );
    };

    function leftNav() {
        return (
            <Box
                sx={{
                    width: isMenuCollapsed ? "0" : { lg: "500px", md: "425px", xs: "350px" },
                    height: "100%",
                    transition: "all 0.3s ease-in-out",
                    overflowY: "auto",
                    backgroundColor: "#002A6E",
                    flexShrink: 0,
                }}
            >
                <Container
                    sx={{
                        mt: 1,
                        px: 2,
                        opacity: isMenuCollapsed ? 0 : 1,
                        transition: "opacity 0.3s ease-in-out",
                        padding: "8px 16px",
                    }}
                >
                    <Stack spacing={2}>
                        <Typography variant="h2" color="secondary" sx={{ pt: 0, pb: 0 }}>
                            Lesson
                        </Typography>

                        {isLoading && showSkeleton ? (
                            <Box sx={{ pt: 0 }}>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <Box key={i} sx={{ display: "flex", mb: 4.5, mt: 1.5, alignItems: "center" }}>
                                        <Skeleton
                                            variant="rectangular"
                                            height={40}
                                            width={36}
                                            sx={{ borderRadius: 1, backgroundColor: "#F2F7FF", ml: 1.5 }}
                                        />
                                        <Skeleton
                                            variant="rectangular"
                                            height={30}
                                            width={256}
                                            sx={{ borderRadius: 1, backgroundColor: "#F2F7FF", ml: 2 }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            courseList.map((course) => {
                                const isCourseSelected = !!(selectedCourse && selectedCourse.id === course.id);
                                const isExpanded = !!course.expanded;

                                const isSettings = isSettingsCourse(course);

                                return isSettings ? (
                                    <Stack
                                        key={course.id}
                                        ref={(el) => (courseRefs.current[course.id] = el)}
                                        direction="row"
                                        alignItems="center"
                                        onClick={() => handleCourseMenuClick(course)}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                borderRadius: "10px",
                                                border: isCourseSelected ? "1.5px solid #FCCD56" : "1.5px solid transparent",
                                                p: "10px 12px",
                                                width: "100%",
                                                cursor: "pointer",
                                                transition: "all 0.18s ease",
                                                backgroundColor: isCourseSelected ? "rgba(252,205,86,0.08)" : "transparent",
                                                "&:hover": {
                                                    backgroundColor: isCourseSelected
                                                        ? "rgba(252,205,86,0.12)"
                                                        : "rgba(242,247,255,0.06)",
                                                    border: isCourseSelected
                                                        ? "1.5px solid #FCCD56"
                                                        : "1.5px solid rgba(242,247,255,0.2)",
                                                },
                                                ...(isCourseSelected && {
                                                    ...jiggleKeyframes,
                                                    animation: jiggling ? 'jiggle 0.4s ease-in-out' : 'none',
                                                }),
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" sx={{ width: 'fit-content' }}>
                                                <img
                                                    src={isCourseSelected ? SettingsOrangeIcon : SettingsWhiteIcon}
                                                    style={{ maxWidth: "36px", maxHeight: "36px", marginLeft: "2px" }}
                                                />
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        color: isCourseSelected ? "#FCCD56" : "#F2F7FF",
                                                        ml: 1.5,
                                                        fontWeight: isCourseSelected ? 600 : 500,
                                                        fontSize: "0.95rem",
                                                    }}
                                                >
                                                    {course.name}
                                                </Typography>
                                            </Stack>
                                            {calculateUserDialogPromptIcon(course.userDialogPromptCount, course.dialogPromptCount)}
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <div key={course.id} ref={(el) => (courseRefs.current[course.id] = el)}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            onClick={() => handleCourseMenuClick(course)}
                                            justifyContent="space-between"
                                            sx={{
                                                backgroundColor: isCourseSelected ? "#FCCD56" : "transparent",
                                                borderRadius: "10px",
                                                border: isCourseSelected
                                                    ? "1.5px solid transparent"
                                                    : isSettingsComplete()
                                                        ? "1.5px solid rgba(242,247,255,0.3)"
                                                        : "1.5px solid rgba(242,247,255,0.15)",
                                                p: "10px 12px",
                                                cursor: "pointer",
                                                transition: "all 0.18s ease",
                                                "&:hover": {
                                                    backgroundColor: isCourseSelected
                                                        ? "#f5c840"
                                                        : "rgba(242,247,255,0.07)",
                                                    border: isCourseSelected
                                                        ? "1.5px solid transparent"
                                                        : "1.5px solid rgba(242,247,255,0.4)",
                                                },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleCourseArrowClick(e, course)}
                                                    sx={{
                                                        color: isCourseSelected ? "#251C00" : "#F2F7FF",
                                                        transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                                        transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1)",
                                                        p: 0.5,
                                                        "&:hover": {
                                                            backgroundColor: isCourseSelected
                                                                ? "rgba(37,28,0,0.1)"
                                                                : "rgba(242,247,255,0.1)",
                                                        },
                                                    }}
                                                >
                                                    <ExpandMoreIcon fontSize="small" />
                                                </IconButton>

                                                <img
                                                    src={isCourseSelected ? LargeBlackIcon : LargeWhiteIcon}
                                                    style={{
                                                        maxWidth: "32px",
                                                        maxHeight: "32px",
                                                        opacity: isCourseSelected ? 1 : isSettingsComplete() ? 0.9 : 0.45,
                                                        transition: "opacity 0.18s ease",
                                                    }}
                                                />

                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        color: isCourseSelected
                                                            ? "#251C00"
                                                            : isSettingsComplete()
                                                                ? "#F2F7FF"
                                                                : "rgba(242,247,255,0.5)",
                                                        ml: 1,
                                                        fontSize: "1rem",
                                                        fontWeight: isCourseSelected ? 700 : 500,
                                                        transition: "color 0.18s ease",
                                                    }}
                                                >
                                                    {course.name}
                                                </Typography>
                                            </Stack>
                                            <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
                                                {calculateUserDialogPromptIcon(course.userDialogPromptCount, course.dialogPromptCount)}
                                            </Box>
                                        </Stack>

                                        {/* Lessons under course */}
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Stack sx={{ mt: 0.5 }}>
                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        "&::before": {
                                                            content: '""',
                                                            position: "absolute",
                                                            left: "28px",
                                                            top: 0,
                                                            bottom: 0,
                                                            width: "1.5px",
                                                            backgroundColor: "rgba(242,247,255,0.12)",
                                                            borderRadius: "2px",
                                                        },
                                                    }}
                                                >
                                                    {lessonList
                                                        .filter((lesson) => lesson.course_id === course.id && !lesson.is_hidden)
                                                        .map((lesson) => {
                                                            const isLessonSelected = selectedLesson && selectedLesson.id === lesson.id;
                                                            const isLessonExpanded = !!lesson.expanded;

                                                            return (
                                                                <div key={lesson.id} ref={(el) => (lessonRefs.current[lesson.id] = el)}>
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        onClick={() => handleLessonMenuClick(lesson)}
                                                                        justifyContent="space-between"
                                                                        sx={{
                                                                            border: isLessonSelected
                                                                                ? "1.5px solid #FCCD56"
                                                                                : "1.5px solid rgba(242,247,255,0.18)",
                                                                            borderRadius: "8px",
                                                                            p: "8px 10px",
                                                                            mt: 1,
                                                                            ml: "52px",
                                                                            cursor: "pointer",
                                                                            transition: "all 0.15s ease",
                                                                            backgroundColor: isLessonSelected
                                                                                ? "rgba(252,205,86,0.07)"
                                                                                : "transparent",
                                                                            "&:hover": {
                                                                                backgroundColor: isLessonSelected
                                                                                    ? "rgba(252,205,86,0.12)"
                                                                                    : "rgba(242,247,255,0.05)",
                                                                                border: isLessonSelected
                                                                                    ? "1.5px solid #FCCD56"
                                                                                    : "1.5px solid rgba(242,247,255,0.3)",
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(e) => handleLessonArrowClick(e, lesson)}
                                                                                sx={{
                                                                                    color: isLessonSelected ? "#FCCD56" : "rgba(242,247,255,0.7)",
                                                                                    transform: isLessonExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                                                                    transition: "transform 0.22s cubic-bezier(0.4,0,0.2,1)",
                                                                                    p: 0.4,
                                                                                    "&:hover": {
                                                                                        backgroundColor: "rgba(242,247,255,0.08)",
                                                                                    },
                                                                                }}
                                                                            >
                                                                                <ExpandMoreIcon sx={{ fontSize: "1.1rem" }} />
                                                                            </IconButton>
                                                                            <img
                                                                                src={isLessonSelected ? MediumOrangeIcon : MediumWhiteIcon}
                                                                                style={{
                                                                                    maxWidth: "24px",
                                                                                    maxHeight: "24px",
                                                                                    opacity: isLessonSelected ? 1 : 0.8,
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                variant="h5"
                                                                                sx={{
                                                                                    color: isLessonSelected ? "#FCCD56" : "#F2F7FF",
                                                                                    ml: 0.75,
                                                                                    fontSize: "0.9rem",
                                                                                    fontWeight: isLessonSelected ? 600 : 400,
                                                                                    transition: "color 0.15s ease",
                                                                                }}
                                                                            >
                                                                                {lesson.name}
                                                                            </Typography>
                                                                        </Stack>
                                                                        <Box sx={{ ml: 1, display: "flex", alignItems: "center", flexShrink: 0 }}>
                                                                            {calculateUserDialogPromptIcon(lesson.userDialogPromptCount, lesson.dialogPromptCount)}
                                                                        </Box>
                                                                    </Stack>

                                                                    {/* Dialogs under lesson */}
                                                                    {!isReviewLesson(course, lesson) && (
                                                                        <Collapse in={isLessonExpanded} timeout="auto" unmountOnExit>
                                                                            <Stack sx={{ mt: 0.5 }}>
                                                                                <Box
                                                                                    sx={{
                                                                                        position: "relative",
                                                                                        "&::before": {
                                                                                            content: '""',
                                                                                            position: "absolute",
                                                                                            left: "75px",
                                                                                            top: 0,
                                                                                            bottom: 0,
                                                                                            width: "1.5px",
                                                                                            backgroundColor: "rgba(242,247,255,0.08)",
                                                                                            borderRadius: "2px",
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    {dialogList
                                                                                        .filter((dialog) => dialog.lesson_id === lesson.id)
                                                                                        .map((dialog) => {
                                                                                            const isDialogSelected = selectedDialog && selectedDialog.id === dialog.id;
                                                                                            return (
                                                                                                <Stack
                                                                                                    key={dialog.id}
                                                                                                    direction="row"
                                                                                                    alignItems="center"
                                                                                                    onClick={() => handleDialogMenuClick(dialog)}
                                                                                                    justifyContent="space-between"
                                                                                                    sx={{
                                                                                                        border: isDialogSelected
                                                                                                            ? "1.5px solid #FCCD56"
                                                                                                            : "1.5px solid rgba(242,247,255,0.12)",
                                                                                                        borderRadius: "8px",
                                                                                                        p: "7px 10px",
                                                                                                        mt: 0.75,
                                                                                                        ml: "96px",
                                                                                                        cursor: "pointer",
                                                                                                        transition: "all 0.15s ease",
                                                                                                        backgroundColor: isDialogSelected
                                                                                                            ? "rgba(252,205,86,0.07)"
                                                                                                            : "transparent",
                                                                                                        "&:hover": {
                                                                                                            backgroundColor: isDialogSelected
                                                                                                                ? "rgba(252,205,86,0.12)"
                                                                                                                : "rgba(242,247,255,0.04)",
                                                                                                            border: isDialogSelected
                                                                                                                ? "1.5px solid #FCCD56"
                                                                                                                : "1.5px solid rgba(242,247,255,0.22)",
                                                                                                        },
                                                                                                    }}
                                                                                                >
                                                                                                    <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
                                                                                                        <img
                                                                                                            src={isDialogSelected ? SmallOrangeIcon : SmallWhiteIcon}
                                                                                                            style={{
                                                                                                                maxWidth: "22px",
                                                                                                                maxHeight: "22px",
                                                                                                                flexShrink: 0,
                                                                                                                opacity: isDialogSelected ? 1 : 0.7,
                                                                                                            }}
                                                                                                        />
                                                                                                        <Stack direction="column" spacing={0} sx={{ ml: 1, minWidth: 0 }}>
                                                                                                            <Typography
                                                                                                                variant="h6"
                                                                                                                sx={{
                                                                                                                    color: isDialogSelected ? "#FCCD56" : "#F2F7FF",
                                                                                                                    whiteSpace: "normal",
                                                                                                                    wordBreak: "break-word",
                                                                                                                    overflowWrap: "break-word",
                                                                                                                    fontSize: "0.8rem",
                                                                                                                    fontWeight: isDialogSelected ? 600 : 400,
                                                                                                                    lineHeight: 1.35,
                                                                                                                    transition: "color 0.15s ease",
                                                                                                                }}
                                                                                                            >
                                                                                                                {dialog.title}
                                                                                                            </Typography>
                                                                                                            <Typography
                                                                                                                variant="body2"
                                                                                                                sx={{
                                                                                                                    color: "darkgrey",
                                                                                                                    whiteSpace: "normal",
                                                                                                                    wordBreak: "break-word",
                                                                                                                    overflowWrap: "break-word",
                                                                                                                    fontSize: "0.75rem",
                                                                                                                    lineHeight: 1.3,
                                                                                                                    mt: 0.25,
                                                                                                                }}
                                                                                                            >
                                                                                                                {dialog.subtitle}
                                                                                                            </Typography>
                                                                                                        </Stack>
                                                                                                    </Stack>
                                                                                                    <Box sx={{ ml: 1, display: "flex", alignItems: "center", flexShrink: 0 }}>
                                                                                                        {calculateUserDialogPromptIcon(dialog.userDialogPromptCount, dialog.dialogPromptCount)}
                                                                                                    </Box>
                                                                                                </Stack>
                                                                                            );
                                                                                        })}
                                                                                </Box>
                                                                            </Stack>
                                                                        </Collapse>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                </Box>
                                            </Stack>
                                        </Collapse>
                                    </div>
                                );
                            })
                        )}
                    </Stack>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: "flex",
            width: "100%",
            height: "100%",
            position: "relative",
            overflow: "hidden",
            pr: 2,
        }}>
            {/* Left NAV */}
            {isTouchDevice ? (
                <SwipeableDrawer
                    anchor="left"
                    open={!isMenuCollapsed}
                    onClose={() => setIsMenuCollapsed(true)}
                    onOpen={() => setIsMenuCollapsed(false)}
                    disableDiscovery={false}
                    disableSwipeToOpen={false}
                    swipeAreaWidth={35} // width of the swipe trigger area
                    SwipeAreaProps={{
                        sx: { left: 24 }, // <-- leave ~24px for browser back-swipe
                    }}
                    PaperProps={{
                        sx: { backgroundColor: "#002A6E" },
                    }}
                    >
                    {leftNav()}
                    {/* swipe hint footer */}
                    <Stack direction="column" sx={{ width: '100%', color: "#FFFFFFBB", userSelect: "none", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                        <Typography>Swipe left to close</Typography>
                        <Typography>Swipe right to open</Typography>
                    </Stack>

                </SwipeableDrawer>

            ) : (
                <Box sx={{
                    width: isMenuCollapsed ? "0" : { lg: "500px", md: "425px", xs: "350px" },
                }}>
                    {leftNav()}
                </Box>
            )}

            {/* Main CONTENT */}
            <Box sx={{
                width: isMenuCollapsed ? "100%" : "66.67%",
                transition: "all 0.3s ease-in-out",
                flexGrow: 1,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: "#F2F7FF",
                pr: 2,
            }}>
                {/* Main content HEADER */}
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%", py: 2, pr: isMobile ? 2 : 4 }} // padding for left/right spacing
                >
                    {/* header LEFT SIDE */}
                    <Stack direction="row" alignItems="center" sx={{ width: isMobile ? "100%" : "auto", px: 2 }}>
                        <Button
                            onClick={onToggleMenuCollapse}
                            sx={{
                                ml: 2.5,
                                minWidth: "40px",
                                height: "40px",
                                borderRadius: "4px",
                                backgroundColor: "transparent",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                            }}>
                            <img src={isMenuCollapsed ? SidebarExpandIcon : SidebarCollapseIcon} style={{ width: "24px", height: "24px" }} alt={isMenuCollapsed ? "Expand Menu" : "Collapse Menu"} />
                        </Button>
                        {isLoading && showSkeleton ? (
                            <Skeleton
                                variant="rectangular"
                                height={40}
                                width={200}
                                sx={{
                                    borderRadius: 1,
                                    backgroundColor: "#EEEEEE",
                                    ml: '24px'
                                }}
                            />
                        ) : selectedDialog != null ? (
                            <Typography variant="h2" color="#002A6E" sx={{ marginLeft: "24px" }}>
                                {selectedDialog ? selectedDialog.title : "\u00A0" /* Non-breaking space if null */}
                            </Typography>
                        ) : selectedLesson != null ? (
                            <Typography variant="h2" color="#002A6E" sx={{ marginLeft: "24px" }}>
                                {selectedLesson ? selectedLesson.name : "\u00A0" /* Non-breaking space if null */}
                            </Typography>
                        ) : selectedCourse != null && (
                            <Typography variant="h2" color="#002A6E" sx={{ marginLeft: "24px" }}>
                                {selectedCourse ? selectedCourse.name : "\u00A0" /* Non-breaking space if null */}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
                {/* Main CONTENT component */}
                <Box
                    sx={{
                        display: "flex",
                        flex: 1,
                        minHeight: 0,
                        flexDirection: "column",
                        justifyContent: "start",
                        alignItems: "center",
                        alignSelf: "stretch",
                        width: "100%",
                        overflow: "hidden",
                        backgroundColor: "#FFFFFF",
                        marginLeft: 2,
                        marginRight: 2,
                        borderRadius: "16px",
                    }}>
                    {(() => {
                        if (isLoading && showSkeleton) {
                            return <Stack sx={{ width: '100%', margin: '0 auto', height: '100%' }}>
                                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                                        justifyContent="flex-start"
                                        spacing={2}
                                        sx={{ width: '100%', p: 2 }}
                                    >
                                        {/* Circle Skeleton */}
                                        <Skeleton
                                            variant="circular" // 👈 this makes it a perfect circle
                                            width={60}          // 👈 width and height should match
                                            height={60}
                                            sx={{
                                                borderRadius: '50%', // technically not needed, 'circular' sets this
                                            }}
                                        />
                                        {/* Wide Rectangle Skeleton */}
                                        <Skeleton
                                            variant="rectangular"
                                            height={40}
                                            width={200}
                                            sx={{
                                                borderRadius: 2,
                                                ml: 2, // margin-left between circle and rectangle
                                            }}
                                        />
                                    </Stack>
                                </div>
                            </Stack>
                        }
                        if (selectedDialog != null) {
                            return <DialogPromptFooterProvider key={selectedDialog.id}><LessonProvider>
                                <DialogView dialog={selectedDialog} updateUserDialogPromptSummary={updateUserDialogPromptSummary} nextDialogPrompt={nextDialogPrompt} />
                            </LessonProvider></DialogPromptFooterProvider>
                        }
                        if (selectedLesson != null && selectedCourse != null) {
                            if (userDialogPromptReviewResponse != null && isReviewLesson(selectedCourse, selectedLesson)) {
                                return (
                                    <DialogPromptFooterProvider>
                                        <ReviewView
                                            userDialogPromptReviewResponse={userDialogPromptReviewResponse}
                                            lesson={selectedLesson}
                                            reviewCourse={selectedCourse}
                                            updateUserDialogPromptSummary={updateUserDialogPromptSummary}
                                        />
                                    </DialogPromptFooterProvider>
                                );
                            } else {
                                return <LessonView lesson={selectedLesson} />;
                            }
                        }
                        if (selectedCourse != null) {
                            if (isSettingsCourse(selectedCourse)) {
                                return <DialogPromptFooterProvider><SettingsView course={selectedCourse} updateUserDialogPromptSummary={updateUserDialogPromptSummary} /></DialogPromptFooterProvider>
                            } else {
                                return <CourseView course={selectedCourse} />
                            }
                        }
                        return <div />;
                    })()}
                </Box>
            </Box>
        </Box>
    );
}
